// Importing directly from 'segments.router' instead of using the library's index file
// because @occc-app/design-system requires `routeCreator` from `@occc-app/feat-segments`,
// and `@occc-app/feat-segments` imports components from `@occc-app/design-system`.
// This direct import avoids the circular dependency issue during module resolution in tests with Vite.
import { segmentsRouteCreatorFactory } from '@occc/feat-segments/segments.router';
import { routeCreator as transactionsRouteCreator } from '@occc-app/routes/Transactions/utils';
import { routeCreator as audiencesRouteCreator } from '@occc-app/routes/Audience/utils';
import { routeCreator as memberRouteCreator } from '@occc-app/routes/Member/utils';
import { routeCreator as locationRouteCreator } from '@occc-app/routes/Location/utils';
import { routeCreator as walletRouteCreator } from '@occc-app/routes/Wallet/utils';
import { routeCreator as surveyRouteCreator } from '@occc-app/routes/Survey/routeCreator';
import { routeCreator as productGroupsRouteCreator } from '@occc-app/routes/ProductGroup/utils';
import { routeCreator as awardCatalogRouteCreator } from '@occc-app/routes/AwardCatalog/utils';
import { routeCreator as displayCategoryRouteCreator } from '@occc-app/routes/DisplayCategory/utils';
import { routeCreator as productCategoryRouteCreator } from '@occc-app/routes/ProductCategory/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { routeCreator as messagingCenterRouteCreator } from '@occc/feat-messaging-center';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { triggeredMessagesSidebarChildLocations } from '@occc/feat-triggered-messages';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { routeCreator as scanDataRouteCreator } from '@occc/feat-scan-data';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { routeCreator as tobaccoApiOffersRouteCreator } from '@occc/feat-tobacco-api-offers';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { routeCreator as storedValueRouteCreator } from '@occc/feat-stored-value/features/stored-value.utils';
import { Feature } from '@occc/utils-access-control';
import config, { SegmentsVersion } from '@occc/config';

import { BarChartIcon, SiteManagementNavigationIcon } from '@occc/ui-design-system/icons';
import { NavigationItem, NavigationSubItem } from './types';

export const dashboardNavigationItem: NavigationItem = {
  title: 'Dashboard',
  location: '/',
  Icon: BarChartIcon,
  accessControlKey: Feature.DASHBOARD_VIEW,
};

export const siteManagementNavigationItem: NavigationItem = {
  title: 'Site Management',
  location: locationRouteCreator.list(),
  Icon: SiteManagementNavigationIcon,
  accessControlKey: Feature.LOCATIONS_VIEW,
};

export const buildActivateNavigationItems = (
  segmentsVersion: SegmentsVersion,
  isTriggeredMessagesEnabled: boolean,
) => {
  const { routeCreator: segmentsRouteCreator } = segmentsRouteCreatorFactory(segmentsVersion);

  const activateNavigationItems: NavigationSubItem[] = [
    {
      title: 'Members',
      location: memberRouteCreator.base(),
      accessControlKey: Feature.MEMBERS_VIEW,
    },
    {
      title: 'Dynamic Audiences',
      location: audiencesRouteCreator.base(),
      accessControlKey: Feature.AUDIENCES_VIEW,
    },
    {
      title: 'Surveys',
      location: surveyRouteCreator.list(),
      accessControlKey: Feature.SURVEYS_VIEW,
    },
    {
      title: 'Campaign Management',
      location: '/campaigns',
      accessControlKey: Feature.CAMPAIGNS_VIEW,
    },
    {
      title: 'Segments',
      location: segmentsRouteCreator.list(),
      accessControlKey: Feature.SEGMENTS_VIEW,
    },
    {
      title: 'Program Management',
      // TODO: some tests are fail in this place location: programManagementRouteCreator.base(),
      location: '/program-management',
      accessControlKey: Feature.PROGRAM_MANAGEMENT_VIEW,
      additionalChildLocations: [
        // additionalChildLocations only necessary if the child page route is outside the scope of the parent route.
        productGroupsRouteCreator.list(),
        awardCatalogRouteCreator?.base(),
        displayCategoryRouteCreator.list(),
        productCategoryRouteCreator.list(),
        storedValueRouteCreator.base,
      ],
    },
  ];

  if (config.features.walletSteeringEnabled) {
    activateNavigationItems.unshift({
      title: 'Wallets',
      location: walletRouteCreator.list(),
      accessControlKey: Feature.WALLETS_VIEW,
    });
  }

  if (!!config.features?.messagingCenterEnabled || isTriggeredMessagesEnabled) {
    activateNavigationItems.push({
      title: 'Messaging Center',
      location: messagingCenterRouteCreator.base(),
      accessControlKey: [Feature.MESSAGING_CENTER_VIEW, Feature.TRIGGERED_MESSAGES_VIEW],
      additionalChildLocations: isTriggeredMessagesEnabled
        ? triggeredMessagesSidebarChildLocations
        : undefined,
    });
  }

  return activateNavigationItems;
};

export const buildTobaccoNavigationItems = ({
  isScanDataEnabled,
  isExternalTobaccoApiOfferEnabled,
}: {
  isScanDataEnabled: boolean;
  isExternalTobaccoApiOfferEnabled: boolean;
}): NavigationSubItem[] => {
  const items: NavigationSubItem[] = [];

  if (isScanDataEnabled) {
    items.push({
      title: 'Scan Data',
      location: scanDataRouteCreator.base(),
      accessControlKey: Feature.SCAN_DATA_VIEW,
    });
  }

  if (isExternalTobaccoApiOfferEnabled) {
    items.push({
      title: 'Tobacco API Offers',
      location: tobaccoApiOffersRouteCreator.base(),
      accessControlKey: Feature.TOBACCO_API_OFFERS_VIEW,
    });
  }

  return items;
};

export const touchpointNavigationItems: NavigationSubItem[] = [
  {
    title: 'Schedule',
    location: '/touchpoint/schedule',
    accessControlKey: Feature.TOUCHPOINT_VIEW,
  },
  {
    title: 'Default Screen',
    location: '/touchpoint/default-screen',
    accessControlKey: Feature.TOUCHPOINT_VIEW,
  },
  {
    title: 'Assets',
    location: '/touchpoint/assets',
    accessControlKey: Feature.TOUCHPOINT_VIEW,
  },
  {
    title: 'Ad Sets',
    location: '/touchpoint/ad-sets',
    accessControlKey: Feature.TOUCHPOINT_VIEW,
  },
];

export const transactNavigationItems: NavigationSubItem[] = [];

if (config.features.mobileTransactionsEnabled) {
  transactNavigationItems.push({
    title: 'Mobile Transaction Admin',
    location: transactionsRouteCreator.base(),
    accessControlKey: Feature.TRANSACTIONS_VIEW,
  });
}
